/**
 * The deck's icon set.
 *
 * Inline SVG, not generated images. His mock is iconographic and he offered Magnific
 * for the assets, but raster is the wrong medium here for three reasons that all bite
 * at once: an icon has to stay crisp at 16px, it has to recolour with the light/dark
 * toggle, and this is an offline-first PWA where twelve bundled PNGs is real weight.
 * `stroke="currentColor"` gets the theme for free, which no raster can do.
 *
 * One geometry for all of them: a 24-box, 1.6 stroke, round caps and joins. Mixed
 * stroke weights are the single thing that makes a hand-assembled icon set look
 * assembled, so the weight is set here once and never per-icon.
 */

export type IconName =
  | 'help'
  | 'action'
  | 'agenda'
  | 'graph'
  | 'objective'
  | 'agent'
  | 'judgement'
  | 'update'
  | 'holdings'
  | 'search'
  | 'notes'
  | 'inbox'
  | 'project'
  | 'clock'
  | 'chevron'
  | 'owed'
  | 'waiting'
  // Shell icons, added when the topbar's emoji were replaced. Same 24-unit grid and the same
  // single-stroke construction as the rest, so the two halves of the app share one vocabulary.
  | 'menu'
  | 'home'
  | 'brain'
  | 'spark'
  | 'plus'
  | 'download'
  | 'share'
  | 'link'
  /*
   * The notes app's own chrome, added 2026-08-26. It was drawn in EMOJI: a folder, a magnifying
   * glass, a gear, a pencil, a book, a star, forty-eight of them across the shell, sitting beside
   * the deck's stroked outlines. Emoji are colour glyphs rendered by the operating system, so they
   * carry a different weight, a different palette and a different shape on every device, which is
   * why the two halves of the app read as two products. Same 24-box and same single stroke as
   * everything above, so they inherit the theme and the toggle for free.
   */
  | 'folder'
  | 'tag'
  | 'settings'
  | 'edit'
  | 'code'
  | 'reading'
  | 'star'
  | 'canvas'
  | 'table'
  | 'attachment'
  | 'close'
  /* A decorative sigil for frame furniture. Never labels a control. */
  | 'sigil'
  // The dialogs and secondary panels, second pass of the same sweep.
  | 'expand'
  | 'send'
  | 'check'
  | 'warning'
  | 'sun'
  | 'moon'
  | 'checkbox'
  | 'camera'
  | 'contrast';

/** Path data only. The wrapper owns size, stroke and colour so they cannot drift. */
const PATHS: Record<IconName, string> = {
  // A check inside a circle: something to finish.
  action: 'M12 3a9 9 0 1 0 9 9M8.5 12.5l2.5 2.5L21 5',
  agenda: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  // Three linked nodes, which is what the constellation panel actually draws.
  graph: 'M6 7.5a2 2 0 1 0 0-.1M18 6.5a2 2 0 1 0 0-.1M12 18a2 2 0 1 0 0-.1M7.6 8.9 11 16M16.6 8.1 13 16',
  objective: 'M12 3v18M3 12h18M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9',
  /*
   * A helmeted head with a narrow T-shaped visor slit, redrawn 2026-08-27. It was a boxy robot
   * face with two dot eyes, which is the same picture every product uses for "bot" and read as
   * generic beside a board that is otherwise all hard angles. This is the only icon in the set
   * whose subject is the crew, so it is the one place the reference belongs in the iconography
   * rather than in the frame. Deliberately geometric: a dome, a jaw, and the slit.
   */
  agent: 'M6 12a6 6 0 0 1 12 0v6.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 18.5zM9 10.5h6v2.5h-2v3h-2v-3H9z',
  // A question mark: this panel asks, it does not tell.
  judgement: 'M9.2 9a3 3 0 1 1 4 2.8c-.8.4-1.2 1-1.2 1.9v.6M12 17.5v.01M12 3a9 9 0 1 0 9 9',
  update: 'M4 12a8 8 0 0 1 13.7-5.6M20 12a8 8 0 0 1-13.7 5.6M17 3v4h-4M7 21v-4h4',
  holdings: 'M4 7.5 12 4l8 3.5-8 3.5zM4 12l8 3.5 8-3.5M4 16.5 12 20l8-3.5',
  search: 'M10.5 4a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13M15.5 15.5 21 21',
  /*
   * A DATAPAD, redrawn 2026-08-28. It was a sheet of paper with a folded corner and two ruled
   * lines, which is the same picture every product uses for "document" and reads as office
   * software beside a board made of chamfered plate. A slab with a screen and a control strip
   * says the same thing in this register and survives 14px, where the folded corner does not.
   */
  notes: 'M6 3h12v18H6zM8.5 6h7v9h-7zM10 18h4',
  /* An open manual: two leaves and a spine. Kept in the same flat, stencilled
     language as the datapad above rather than a question mark in a circle. */
  help: 'M4 5h6a2 2 0 0 1 2 2v12a2 2 0 0 0-2-2H4zM20 5h-6a2 2 0 0 0-2 2v12a2 2 0 0 1 2-2h6z',
  inbox: 'M4 13h4l1.5 2.5h5L16 13h4M4 13 6.5 5h11L20 13v6H4z',
  project: 'M4 6h6l2 2h8v11H4zM8 13h8',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 7.5V12l3 2',
  chevron: 'M6 9.5 12 15l6-5.5',
  // An arrow leaving: he owes it.
  owed: 'M4 12h13M12.5 7l5 5-5 5M20 4v16',
  // An arrow arriving: he is waiting for it.
  waiting: 'M20 12H7M11.5 7l-5 5 5 5M4 4v16',
  // Three rules. Deliberately not a hamburger with rounded caps: this set has square ends.
  menu: 'M4 7h16M4 12h16M4 17h16',
  // A roof over a doorway, which is a house without being a picture of one.
  home: 'M4 11 12 4l8 7M6.5 9.5V20h11V9.5M10 20v-6h4v6',
  // Concentric arcs converging on a core: the same idea the deck's brain panel draws, at 24px.
  brain: 'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7M12 4v3M12 17v3M4.5 12h3M16.5 12h3M6.7 6.7l2.1 2.1M15.2 15.2l2.1 2.1M17.3 6.7l-2.1 2.1M8.8 15.2l-2.1 2.1',
  // A four-pointed spark for the assistant, matching the accent's role as "something computed".
  spark: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z',
  plus: 'M12 5v14M5 12h14',
  download: 'M12 4v11M8 11l4 4 4-4M5 20h14',
  // An arrow leaving a tray: send, rather than a social glyph.
  share: 'M5 12v7h14v-7M12 15V4M8.5 7.5 12 4l3.5 3.5',
  link: 'M9.5 14.5 14.5 9.5M8 12 6 14a3.5 3.5 0 0 0 5 5l2-2M16 12l2-2a3.5 3.5 0 0 0-5-5l-2 2',
  // A folder is `project` without its contents line, which keeps the two obviously related.
  /*
   * A CARGO BAY, not a manila folder. Same silhouette so nothing has to be relearned, but the
   * top-right corner is cut and two ridges run across it: the chamfer is the motif every panel
   * on this board carries, and putting it on the one icon that appears forty times down the rail
   * is worth more than putting it anywhere else.
   */
  folder: 'M4 6h6l2 2h6l2 2v9H4zM4 12h16',
  // A label with its eyelet. The eyelet is a closed micro-arc, so it stays one path like the rest.
  tag: 'M12 4H4v8l8 8 8-8zM7.6 8a.6.6 0 1 0 .01 0',
  /*
   * The helmet again, at frame weight. Shares `agent`'s geometry deliberately: the crew's icon
   * and the rail's mark being the same shape is what makes them read as one house rather than
   * two references. It is never used to label a control, only as furniture.
   */
  sigil: 'M6 12a6 6 0 0 1 12 0v6.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 18.5zM9 10.5h6v2.5h-2v3h-2v-3H9z',
  // A hub with spokes rather than a toothed gear: teeth turn to mush at 16px.
  settings: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6M12 3v3M12 18v3M4.6 7.5l2.6 1.5M16.8 15l2.6 1.5M4.6 16.5l2.6-1.5M16.8 9l2.6-1.5',
  edit: 'M4 20h4L20 8l-4-4L4 16zM15 5l4 4',
  code: 'M9 8 5 12l4 4M15 8l4 4-4 4',
  // An open book: two leaves from one spine, for reading mode.
  reading: 'M12 6c-2-1.7-5-2-8-1v13c3-1 6-.7 8 1 2-1.7 5-2 8-1V5c-3-1-6-.7-8 1M12 6v13',
  star: 'M12 4l2.4 5.1 5.6.8-4 4 1 5.6-5-2.7-5 2.7 1-5.6-4-4 5.6-.8z',
  // A folded map, which is what a canvas of scattered cards is closest to.
  canvas: 'M4 7l5-2 6 2 5-2v12l-5 2-6-2-5 2zM9 5v12M15 7v12',
  table: 'M4 5h16v14H4zM4 10h16M10 10v9',
  attachment: 'M15 7 8.5 13.5a3 3 0 0 0 4.2 4.2L20 10.5a5 5 0 0 0-7-7L5.4 11.1',
  close: 'M6 6l12 12M18 6 6 18',
  // Four corners pushing outward: fit-to-screen, without drawing a screen.
  expand: 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5',
  // A paper dart. `share` is an arrow leaving a tray, which is a different verb.
  send: 'M4 12 20 4l-6 16-2.6-6.4z',
  check: 'M5 12.5l4.5 4.5L19 7',
  warning: 'M12 4l9 16H3zM12 10v4.5M12 17h.01',
  sun: 'M12 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7M12 3v2M12 19v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M3 12h2M19 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon: 'M15.5 3.5a8.5 8.5 0 1 0 5 13.4 9 9 0 0 1-5-13.4',
  checkbox: 'M5 5h14v14H5zM8.5 12l2.5 2.5L15.5 9',
  camera: 'M4 8h3l1.5-2h7L17 8h3v11H4zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7',
  // A circle split down its diameter: follow whatever the system is, rather than choosing.
  contrast: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16M12 4v16',
};

export interface IconProps {
  name: IconName;
  /** Pixel size. 16 for inline meta, 18 for rails, 20 for panel heads. */
  size?: number;
  className?: string;
}

/**
 * Always `aria-hidden`. Every icon in the deck sits beside its own text label, so
 * announcing it again is noise for a screen reader rather than help.
 */
export function Icon({ name, size = 18, className }: IconProps): JSX.Element {
  return (
    <svg
      className={className === undefined ? 'mo-icon' : `mo-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
